import { getAssetPath } from "@stencil/core";
import { DateTimeFactory, Log, AppConfigService as Config } from "../services";
import { AVBusService } from "./bus.service";
import { HandledError } from "../models/HandledError";
import { ErrorCode, ICountryCode } from "../models";
import ky from "ky";
import { IScheduleService } from "../models/schedule/IScheduleService";
import {
  IScheduleRequest,
  IScheduleSecurity,
  IScheduleResponse,
  IScheduledAppointmentTimeFrame,
} from "../models/schedule/ISchedule";
import { ScheduleFactory } from "./schedule/schedule.factory";
import moment from "moment-timezone";

/* removed to have australia work
 {
    "name": "Christmas Island",
    "dial_code": "+61",
    "code": "CX"
},
{
    "name": "Cocos (Keeling) Islands",
    "dial_code": "+61",
    "code": "CC"
},
*/
class ScheduleServiceCtrl implements IScheduleService {
  init() {
    ScheduleFactory.create(Config.optionsInstance());
  }

  create(
    request: IScheduleRequest,
    security: IScheduleSecurity
  ): Promise<IScheduleResponse> {
    return ScheduleFactory.instance.create(request, security);
  }

  /** helpers */

  public getProposedDateTime(): Date {
    const hourNow = DateTimeFactory.create().hour() + 1;
    return DateTimeFactory.create()
      .hour(hourNow)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  public async getCountryDialCodes(): Promise<ICountryCode[]> {
    try {
      const path = getAssetPath(`auvious-assets/country.codes.json`);
      return await ky.get(path).json();
    } catch (ex) {
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.LOCALE_COUNTRY_CODES_FAILED)
      );
      Log.error(ex);
      throw ex;
    }
  }

  private getOverlappingAppointmentsCount(
    startAt: Date,
    duration: moment.Duration
  ): Promise<{ count: number }> {
    return ky
      .get(`${Config.auviousBaseUrl}/api/appointments/overlap/count`, {
        searchParams: {
          applicationId: Config.applicationId,
          organizationId: this.getOrganizationId(),
          start: startAt.toISOString(),
          duration: duration.toISOString(),
        },
      })
      .json();
  }

  private getOrganizationId() {
    switch (Config.scheduleMode) {
      case "genesys-appointment":
        return Config.pcOrganizationId;
      case "talkdesk-appointment":
        return Config.organizationId;
    }
  }

  private get isScheduleOverlapEnabled() {
    return (
      Config.scheduleOverlapCheck &&
      ["genesys-appointment", "talkdesk-appointment"].includes(
        Config.scheduleMode
      )
    );
  }

  public async getScheduledAppointmentsTimeframe(
    startAt: Date,
    timeFrame: moment.Duration
  ): Promise<IScheduledAppointmentTimeFrame[]> {
    if (!this.isScheduleOverlapEnabled) {
      return [];
    }
    try {
      const response: {
        start: string;
        end: string;
        timezone: string;
        count: number;
        duration: string;
      }[] = await ky
        .get(`${Config.auviousBaseUrl}/api/appointments/count`, {
          searchParams: {
            applicationId: Config.applicationId,
            organizationId: this.getOrganizationId(),
            start: startAt.toISOString(),
            timeFrame: timeFrame.toISOString(),
          },
        })
        .json();

      return (
        response
          // remove slots that are haven't reached the max concurrent appointments
          .filter((item) => item.count >= Config.scheduleOverlapMaxConcurrent)
          .map((item) => ({
            start: moment(item.start).tz(item.timezone),
            end: moment(item.end).tz(item.timezone),
            timezone: item.timezone,
            count: item.count,
            duration: moment.duration(item.duration),
          }))
      );
    } catch (ex) {
      Log.error(ex);
      return [];
    }
  }

  public async isOverlappingSchedule(
    request: IScheduleRequest
  ): Promise<boolean> {
    if (!this.isScheduleOverlapEnabled) {
      return false;
    }
    try {
      const response = await this.getOverlappingAppointmentsCount(
        request.start,
        moment.duration(Config.scheduleIntervalMinutes, "minutes")
      );
      return response.count >= Config.scheduleOverlapMaxConcurrent;
    } catch (ex) {
      Log.error(ex);
      return false;
    }
  }
}

export const ScheduleService = new ScheduleServiceCtrl();
